import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PatientService, sharedApiClient } from '../fetcher';
import { useNotifications, isAbortError } from '@medprompt/shared';
import { hasPatientCodesData } from '../detail/PatientDiaryPanel';

const useTaskPatientDiary = ({ enabled, nhsId, patientId }) => {
  const { addNotification } = useNotifications();
  const patientService = useMemo(() => new PatientService(sharedApiClient), []);

  const [patientRecordData, setPatientRecordData] = useState(null);
  const [patientRecordDataError, setPatientRecordDataError] = useState(false);
  const [refreshingPatientCodes, setRefreshingPatientCodes] = useState(false);
  const refreshingPatientCodesRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !nhsId || !patientId) {
      setPatientRecordData(null);
      setPatientRecordDataError(false);
      setRefreshingPatientCodes(false);
      refreshingPatientCodesRef.current = false;
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    setPatientRecordData(null);
    setPatientRecordDataError(false);

    patientService.fetchPatientRecord(nhsId, patientId, { signal: controller.signal })
      .then((record) => {
        if (requestId !== requestIdRef.current) return;
        setPatientRecordData(record);
      })
      .catch((error) => {
        if (isAbortError(error) || requestId !== requestIdRef.current) return;
        console.error('Failed to fetch patient record for task diary:', error);
        setPatientRecordData(null);
        setPatientRecordDataError(true);
      });

    return () => controller.abort();
  }, [enabled, nhsId, patientId, patientService]);

  const handleRefreshPatientCodes = useCallback(async () => {
    if (!nhsId || !patientId || refreshingPatientCodesRef.current) return;
    const requestId = requestIdRef.current;
    refreshingPatientCodesRef.current = true;
    setRefreshingPatientCodes(true);
    try {
      const codes = await patientService.fetchFreshPatientCodes(nhsId, patientId);
      if (requestId !== requestIdRef.current) return;
      if (!hasPatientCodesData(codes)) {
        addNotification(`task-patient-codes-refresh-empty-${patientId}`, 'Could not refresh problems from EMIS', { isError: true });
        return;
      }
      setPatientRecordData((previous) => (
        previous ? { ...previous, patientCodesData: codes } : previous
      ));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to refresh patient codes from task diary:', error);
      addNotification(`task-patient-codes-refresh-error-${patientId}`, 'Failed to refresh problems from EMIS', { isError: true });
    } finally {
      refreshingPatientCodesRef.current = false;
      if (requestId === requestIdRef.current) {
        setRefreshingPatientCodes(false);
      }
    }
  }, [nhsId, patientId, patientService, addNotification]);

  return {
    patientRecordData,
    patientRecordDataError,
    refreshingPatientCodes,
    handleRefreshPatientCodes,
  };
};

export default useTaskPatientDiary;
